import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * 網站分析
 * ═══════════════════════════════════════════════════════════════
 *
 * GA4 的標籤本身裝在 index.html（要在第一次繪製前就決定同意狀態），
 * 這裡處理兩件它自己做不到的事。
 *
 * ── 一、單頁應用的瀏覽事件 ──────────────────────────────────
 *
 * gtag 預設只在腳本載入時送一次 page_view。這是多頁網站的假設：
 * 換頁 = 重新載入。我們的路由是前端切換的，所以那個假設不成立 ——
 * 不接管的話，報表上每個工作階段都只有一頁，跳出率會虛高到 90%
 * 以上，而且沒有人會發現數字是錯的。
 *
 * 所以 index.html 關掉 send_page_view，改由這裡在每次路由變動時送。
 *
 * ── 二、把同意狀態改回去 ────────────────────────────────────
 *
 * 同意不是一次性的。使用者按了拒絕之後要真的停止量測，
 * 而且下次回來仍然是拒絕的狀態 —— 所以選擇存在 localStorage，
 * index.html 開頭會讀它。
 */

const STORAGE_KEY = "colouredev.analytics";

type Gtag = (...args: unknown[]) => void;

function gtag(...args: unknown[]) {
  const fn = (window as unknown as { gtag?: Gtag }).gtag;
  // 廣告攔截器會讓 gtag 不存在。那不是錯誤，網站要照常運作。
  fn?.(...args);
}

export type AnalyticsChoice = "granted" | "denied";

/** 使用者做過選擇嗎？沒有的話走 index.html 的地區預設值。 */
export function readAnalyticsChoice(): AnalyticsChoice | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

export function setAnalyticsChoice(choice: AnalyticsChoice) {
  try {
    window.localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    /* 無痕模式存不了。這一次的選擇仍然生效，只是下次要再問一遍 */
  }
  gtag("consent", "update", { analytics_storage: choice });
}

/**
 * 每次路由變動送一次 page_view。
 *
 * 只送路徑不送查詢字串 —— 搜尋關鍵字、篩選條件都在 query 裡，
 * 那些是使用者輸入的內容，沒有理由送到第三方去。
 */
export function usePageViews() {
  const location = useLocation();

  useEffect(() => {
    gtag("event", "page_view", {
      page_path: location.pathname,
      page_location: window.location.origin + location.pathname,
      page_title: document.title,
    });
  }, [location.pathname]);
}
